import { DatePipe } from '@angular/common';
import { Component, OnInit, signal, ViewChild, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { UserPreference } from '../../../../models/api/core/preferences/user-preference';
import {
  NoteTypeInput,
  SubTypesInput,
} from '../../../../models/api/view/claim/documentation/notes/notes-models';
import {
  NotesResponse,
  NoteTypesResponse,
  SubTypesResponse,
} from '../../../../models/api/view/claim/documentation/notes/notes-response-models';
import { NotesSearchInput } from '../../../../models/api/view/claim/documentation/notes/notes-search-input';
import { leaveLink } from '../../../../functions/claim/leaveLink';
import { getExpandSubject } from '../../../../functions/get-expand-subject';
import { Action } from '../../../../models/core/action';
import { HighlightPipe } from '../../../../pipes/highlight.pipe';
import { SanitizeHtmlPipe } from '../../../../pipes/sanitize-html.pipe';
import { ExportService } from '../../../../services/export/export.service';
import {
  NoteItem,
  NotesService,
} from '../../../../services/notes/notes.service';
import { SettingsService } from '../../../../services/settings/settings.service';
import { Snackbar } from '../../../../services/snackbar/snackbar.service';
import { ClaimExportMapper } from '../../../../utilities/claim-export-mapper';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { IconButtonComponent } from '../../../core/buttons/icon-button.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { TagComponent } from '../../../core/labels/tag.component';
import { ToastComponent } from '../../../core/labels/toast.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { ExpandPanelComponent } from '../../../core/panels/expand-panel.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../../core/panels/loading-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import {
  CustomButton,
  TableComponent,
} from '../../../core/table/table.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-notes',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    SelectComponent,
    ReactiveFormsModule,
    ButtonComponent,
    PageHeaderComponent,
    TagComponent,
    IconButtonComponent,
    ToastComponent,
    ExpandPanelComponent,
    ActionRowComponent,
    LoadingPanelComponent,
    HighlightPipe,
    SanitizeHtmlPipe,
  ],
  template: `
    <app-page-header label="Notes" [level]="1" appearance="h2">
      <ng-template #left>
        <app-action-row>
          @if (showCreateNote) {
            <app-button
              class="tw-pl-8"
              left1
              (click)="editDraft()"
              label="Create Note"
            />
          }
        </app-action-row>
      </ng-template>
      <ng-template #right>
        @if (showLevel) {
          <div class="tw-flex tw-items-center ">
            <div class="tw-grow tw-min-w-64">
              <app-select
                [options]="noteLevels"
                optionLabel="description"
                optionValue="value"
                class="tw-basis-1/5"
                [virtualScroll]="false"
                [control]="levelControl"
                label="Level"
                [multiple]="false"
                [filter]="false"
                [showClear]="false"
                [showHelpText]="false"
              />
            </div>
          </div>
        }
      </ng-template>
    </app-page-header>

    <div>
      @if (showSentToast) {
        <div class="tw-flex tw-justify-center">
          <app-toast
            icon="fa-light fa-thumbs-up"
            type="Success"
            [dismissSeconds]="10"
            [dismiss]="true"
            (dismissed)="showSentToast = false"
            >Your note has been sent and a diary has been created for the
            examiner</app-toast
          >
        </div>
      }
    </div>
    <app-loading-panel
      [state]="loadingState"
      loadingStyle="Skeleton"
      [loadingHeight]="450"
      [error]="error()"
    >
      <ng-template #loaded>
        <app-table
          #noteTable
          tableId="documentation-notes"
          [dataSource]="notes()"
          [pageSize]="10"
          [pageSizeOptions]="[5, 10, 20, 100]"
          [persistPaginationSettings]="true"
          [persistExpandAll]="true"
          [filterLabelPreferNamedRow]="showCreatorNames"
          [showExport]="true"
          [scrollable]="true"
          scrollHeight="1000px"
          [showFilterButton]="false"
          [globalFilterFields]="['text']"
          filterPlaceholder="Note text"
          filterLabel="Search note text"
          [recordLabel]="'Note'"
          [ariaTableLabel]="'Note'"
          [emptyRecordLabelCase]="'sentence'"
          [pdfExport]="true"
          [expandable]="true"
          [customButtons]="showSaveFilter ? [saveButton] : []"
          [tableFilters]="initialFilters"
          (customButtonClick)="onSaveClicked($event)"
          (filtersChanged)="onFiltersChanged($event)"
          (export)="onExport($event)"
        >
          @if (showDraft) {
            <app-column name="draft" label="Draft" width="16%">
              <ng-template let-data>
                @if (data.draft) {
                  <div class="tw-flex tw-items-center">
                    <app-tag type="Warn">{{ data.draft }}</app-tag>
                    <app-icon-button
                      icon="fa-light fa-edit"
                      ariaLabel="Edit draft"
                      (click)="editDraft(data)"
                    ></app-icon-button>
                  </div>
                }
              </ng-template>
            </app-column>
          }
          <app-column
            name="dateCreated"
            format="d"
            label="Created Date"
            width="16%"
            filterType="date"
          />
          <app-column
            name="createdBy"
            label="Created By"
            width="16%"
            filterType="select"
            [filterLabelName]="showCreatorNames ? 'createdByName' : undefined"
            [filterLabelSeparator]="creatorFilterSeparator"
          />
          <app-column
            name="examiner"
            label="Created For"
            width="16%"
            filterType="select"
            [filterLabelName]="showCreatorNames ? 'examinerName' : undefined"
            [filterLabelSeparator]="creatorFilterSeparator"
          />
          @if (showLevel) {
            <app-column
              name="level"
              label="Level"
              width="16%"
              filterType="select"
            />
          }
          @if (showType) {
            <app-column
              name="type"
              label="Type"
              width="16%"
              filterType="select"
              [customFilterOptions]="noteTypeOptions"
            />
          }
          @if (showLevel) {
            <app-column
              name="subType"
              label="Subtype"
              width="16%"
              filterType="select"
              [customFilterOptions]="subTypeOptions"
            />
          }
          <app-column
            name="text"
            label="Note text"
            [fullRow]="true"
            [fullRowLabel]="false"
          >
            <ng-template #item let-row>
              <app-expand-panel
                [expanded]="noteTable.expanded"
                [label]="getExpandSubject(row.type, row.dateCreated) || 'Note'"
                collapseHeight="114px"
              >
                <div
                  style="white-space: pre-line"
                  [innerHTML]="
                    row.text | highlight: noteTable.searchValue | sanitizeHtml
                  "
                  class="xxtw-leading-normal"
                ></div>
              </app-expand-panel>
            </ng-template>
          </app-column>
        </app-table>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class NotesComponent extends BaseComponent implements OnInit {
  private snackbar = inject(Snackbar);
  notesService = inject(NotesService);
  exportService = inject(ExportService);
  private datePipe = inject(DatePipe);
  private settings = inject(SettingsService);

  /** Accessible subject for note text expand (type + date). */
  protected getExpandSubject = getExpandSubject;

  @ViewChild('noteTable') noteTable: TableComponent;
  levelControl = new FormControl<string>('Claim');
  notes = signal<any[]>([]);

  error = signal<any>(null);
  showSentToast = false;
  showLevel = false;
  showType = false;
  showSaveFilter = false;
  showDraft = false;
  showCreateNote = false;
  showCreatorNames = false;
  readonly creatorFilterSeparator = ' \u2013 ';
  loadingState: LoadingState = 'Loading';

  noteLevels = [
    { description: 'Claim', value: 'Claim' },
    { description: 'Claimant', value: 'Claimant' },
    { description: 'Event', value: 'Event' },
    { description: 'All', value: 'All' },
  ];

  noteTypeOptions: any[] = [];
  subTypeOptions: any[] = [];
  selectedTypeCodes: any = [];
  selectedSubTypeCodes: any = [];
  notesSearchInput: NotesSearchInput;

  saveFilterAction = new Action({
    key: 'save-filter',
    label: 'Save type/subtype filter',
  });

  clearFilterAction = new Action({
    key: 'clear-filter',
    label: 'Delete saved type/subtype filter',
  });

  saveButton = new CustomButton(
    'save-menu',
    'Save filter menu',
    'fa-light fa-save',
    null,
    [this.saveFilterAction, this.clearFilterAction],
  );

  MAX_ROW_COUNT = 2147483647;

  initialFilters: Record<string, any> = {};

  private noteTypes: NoteTypesResponse;

  private subTypes: SubTypesResponse;

  private notesResponse: NotesResponse;

  private noteItem: NoteItem;

  private selectedPrefTypes: string[] = [];

  private selectedPrefSubTypes: string[] = [];

  @HandleLoadingState()
  async ngOnInit() {
    const claim = this.view.currentClaim();
    this.noteItem = this.notesService.getNoteItem(claim);

    this.levelControl.setValue('Claim'); // init level : may update later in loadNoteLevelPreference
    this.showLevel = !(
      claim.lineOfBusiness === 'LA' || claim.lineOfBusiness === 'AD'
    );
    this.showType = !leaveLink(claim.recordSource);
    this.showCreatorNames = !leaveLink(claim.recordSource);

    // Classic Notes.ascx.cs disables the create button when RecordSrc == "L".
    this.showCreateNote =
      !leaveLink(claim.recordSource) && this.view.authorize('CreateNote');

    if (!this.showLevel) {
      this.saveFilterAction.label = 'Save type filter';
      this.clearFilterAction.label = 'Delete saved type filter';
    }
    await this.initialize(claim);
    this.levelControl.valueChanges.subscribe((level) => {
      if (level) {
        this.noteItem.clearNotes();
        this.loadNotes(this.createSearchInput(claim, level));
        this.saveNoteLevelPreference(level);
      }
    });
  }

  @HandleLoadingState({
    loadingProperty: 'loadingState',
    onValue: 'Loading',
    offValue: 'Loaded',
    useViewLoading: true,
  })
  private async initialize(claim: any): Promise<void> {
    await this.loadNoteLevelPreference();
    await this.loadNoteTypePreference();
    await Promise.all([
      this.loadNoteTypes(claim),
      this.loadSubTypes(claim),
      this.loadNotes(this.createSearchInput(claim, this.levelControl.value)),
    ]);
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  private async loadNoteTypePreference(refreshCache = false): Promise<void> {
    const [noteTypePrefs, subTypePrefs] = await Promise.all([
      this.api.preferences.getPreferences(
        {
          type: 'NOTES',
          key: 'NOTE_TYPE',
          value: '',
        },
        {
          cache: true,
          refreshCache,
        },
      ),
      this.api.preferences.getPreferences(
        {
          type: 'NOTES',
          key: 'SUB_TYPE',
          value: '',
        },
        {
          cache: true,
          refreshCache,
        },
      ),
    ]);
    this.selectedPrefTypes = noteTypePrefs.map((p) => p.value);
    this.selectedPrefSubTypes = subTypePrefs.map((p) => p.value);
  }

  private async loadNoteTypes(claim: any): Promise<void> {
    this.noteTypes = await this.noteItem.getNoteTypes(
      this.createNoteTypeInput(claim),
    );
    this.noteTypeOptions = (this.noteTypes?.availableTypes ?? []).map((t) => ({
      label: t.display,
      count: 0,
      value: t.display,
    }));
    this.updateShowSaveFilter(claim);
    if (this.showSaveFilter) {
      const selectedTypes = this.noteTypes?.availableTypes
        .filter((t) => this.selectedPrefTypes.includes(t.code))
        .map((t) => t.display);
      this.applyFilter('type', selectedTypes, 'in', 'and');
    }
  }

  private updateShowSaveFilter(claim: any): void {
    const hasNoteTypes = (this.noteTypes?.availableTypes?.length ?? 0) > 0;

    this.showSaveFilter = !leaveLink(claim.recordSource) && hasNoteTypes;
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  private async loadSubTypes(claim: any): Promise<void> {
    if (!this.showLevel) {
      return;
    }

    this.subTypes = await this.noteItem.getSubTypes(
      this.createSubTypeInput(claim),
    );
    this.subTypeOptions = (this.subTypes?.availableSubTypes ?? []).map((t) => ({
      label: t.display,
      count: 0,
      value: t.display,
    }));
    const selectedSubTypes = this.subTypes?.availableSubTypes
      .filter((t) => this.selectedPrefSubTypes.includes(t.code))
      .map((t) => t.display);
    this.applyFilter('subType', selectedSubTypes, 'in', 'and');
  }

  applyFilter(field: string, value: any, matchMode: string, operator: string) {
    this.initialFilters = {
      ...this.initialFilters,
      [field]: [{ value, matchMode, operator }],
    };
  }

  @HandleLoadingState({
    loadingProperty: 'loadingState',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  private async loadNotes(input: NotesSearchInput): Promise<void> {
    const response = await this.noteItem.getNotes(input);
    this.notesResponse = response;
    this.showDraft = response.drafts.length > 0;
    if (!response) return;
    const drafts = (response.drafts ?? []).map((d) => ({
      ...d,
      draft: 'draft',
      text: d.displayText ?? d.text,
    }));
    this.notes.set([
      ...drafts.map((d) => ({
        ...d,
        dateCreated: d.dateCreated ? new Date(d.dateCreated) : null,
      })),
      ...(response.notes.map((n) => ({
        ...n,
        dateCreated: n.dateCreated ? new Date(n.dateCreated) : null,
      })) ?? []),
    ]);
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  private async loadNoteLevelPreference(): Promise<void> {
    const level = await this.settings.get('notes-note-level');
    this.levelControl.setValue(level ?? 'Claim');
  }

  private saveNoteLevelPreference(level: string) {
    this.settings.set('notes-note-level', level);
  }

  private createSearchInput(claim: any, level: string): NotesSearchInput {
    const commonData = {
      rowStart: 1,
      rowCount: this.MAX_ROW_COUNT,
      sortBy: 'date_create',
      sortDirection: 'DESC',
      includeFilters: true,
      includeDrafts: true,
      initialLoad: false,
      isExport: false,
      text: '',
      exportType: '',
      level,
      claimUID: claim.recordId,
      fileNumber: claim.fileNumber,
      absenceUID: claim.fileNumber,
      employeeUID: claim.hrMasterUid,
      accommodationRequestID: claim.recordId,
      leaveNumber: claim.recordId,
    };
    this.notesSearchInput = {
      searchNotes: commonData,
      key: {
        recordSource: claim.recordSource,
        recordId: claim.recordId,
      },
    };
    return this.notesSearchInput;
  }

  private createNoteTypeInput(claim: any): NoteTypeInput {
    const input: NoteTypeInput = {
      absenceUID: '',
      clientNumber: claim.contractNumber,
    };
    switch (claim.recordSource) {
      case 'J':
        input.claimUID = claim.recordId;
        break;
      case 'T':
        input.absenceUID = claim.fileNumber;
        break;
      case 'R':
        input.accommodationRequestID = claim.recordId;
        break;
    }
    return input;
  }

  private createSubTypeInput(claim: any): SubTypesInput {
    return {
      claimUID: claim.recordId,
      absenceUID: claim.fileNumber,
      accommodationRequestID: claim.recordId,
      contNum: claim.contractNumber,
      level: '',
      types: [],
    };
  }

  private saveFilters() {
    if (this.selectedTypeCodes) {
      const typePref: UserPreference = {
        type: 'NOTES',
        key: 'NOTE_TYPE',
        value: this.selectedTypeCodes.join(','),
      };
      this.api.preferences.setPreference(typePref);
    }

    if (this.selectedSubTypeCodes) {
      const subTypePref: UserPreference = {
        type: 'NOTES',
        key: 'SUB_TYPE',
        value: this.selectedSubTypeCodes.join(','),
      };
      this.api.preferences.setPreference(subTypePref);
    }

    // clear the preferences cache
    this.loadNoteTypePreference(true);
  }

  onFiltersChanged(filters: any): void {
    const selectedType = filters['type']?.[0]?.value;
    const selectedSubType = filters['subType']?.[0]?.value;

    this.selectedTypeCodes = this.noteTypes?.availableTypes
      .filter((t) => selectedType?.includes(t.display))
      .map((t) => t.code);

    this.selectedSubTypeCodes = this.subTypes?.availableSubTypes
      .filter((t) => selectedSubType?.includes(t.display))
      .map((t) => t.code);
  }

  editDraft(draft: any = null) {
    this.noteItem.setSelectedDraft(draft);
    this.router.navigate(['../note-edit'], {
      relativeTo: this.activatedRoute,
    });
  }

  onSaveClicked(event: CustomButton | Action) {
    if ('key' in event) {
      if (event.key === 'save-filter') {
        this.saveFilters();
        this.snackbar.show('Filter saved', 'Success');
      } else {
        this.noteTable.clearColumnFilter('type');
        this.noteTable.clearColumnFilter('subType');
        this.saveFilters();
        this.snackbar.show('Filter deleted', 'Success');
      }
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onExport(format: ExportFormat) {
    let columns = [
      { propertyName: 'dateCreated', headerText: 'Created Date' },
      { propertyName: 'createdBy', headerText: 'Created By' },
      { propertyName: 'examiner', headerText: 'Created For' },
      { propertyName: 'level', headerText: 'Level' },
      { propertyName: 'type', headerText: 'Type' },
      { propertyName: 'subType', headerText: 'Sub Type' },
      { propertyName: 'text', headerText: 'Note Text', stripHtml: true },
    ];
    const excludedProperties = new Set();
    if (!this.showLevel) {
      excludedProperties.add('level');
      excludedProperties.add('subType');
    }
    if (!this.showType) excludedProperties.add('type');
    columns = columns.filter(
      (col) => !excludedProperties.has(col.propertyName),
    );

    const selectedTypes =
      this.noteTypes?.availableTypes
        .filter((t) => this.selectedTypeCodes.includes(t.code))
        .map((t) => t.display) ?? [];

    const selectedSubTypes =
      this.subTypes?.availableSubTypes
        .filter((t) => this.selectedSubTypeCodes.includes(t.code))
        .map((t) => t.display) ?? [];

    let rows = this.notesResponse.notes;
    if (selectedTypes.length || selectedSubTypes.length) {
      const filterMode = selectedTypes.length && selectedSubTypes.length;
      rows = this.notesResponse.notes.filter((n) => {
        if (filterMode) {
          return (
            selectedTypes.includes(n.type) &&
            selectedSubTypes.includes(n.subType)
          );
        } else {
          return (
            selectedTypes.includes(n.type) ||
            selectedSubTypes.includes(n.subType)
          );
        }
      });
    }
    if (!rows.length) {
      this.snackbar.show(
        'No notes to export for the selected filters.',
        'Error',
      );
      return;
    }
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `Notes_${formattedDate}.${format}`;
    if (format === 'csv') {
      const csvData = ClaimExportMapper.toCsvExport(rows, columns, 'Notes');
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = ClaimExportMapper.toExcelExport(
        rows,
        columns,
        'Notes',
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    } else if (format === 'pdf') {
      this.notesSearchInput.searchNotes.types = this.selectedTypeCodes;
      this.notesSearchInput.searchNotes.sTypes = this.selectedSubTypeCodes;
      await this.noteItem.exportPDF(this.notesSearchInput, filename);
    }
  }
}
